import { API_CONFIG } from '../config/api';
import { NotificationHistoryResponse, NotificationHistoryFilters } from '../models/Notification';
import { secureTokenManager } from '../utils/secureTokenManager';

export class NotificationService {
  private baseURL: string;

  constructor() {
    this.baseURL = API_CONFIG.baseURL;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Remove /api prefix from endpoint if baseURL already includes it
    let normalizedEndpoint = endpoint;
    if (this.baseURL.endsWith('/api') && endpoint.startsWith('/api/')) {
      normalizedEndpoint = endpoint.substring(4); // Remove '/api' prefix
    } else if (this.baseURL === '/api' && endpoint.startsWith('/api')) {
      normalizedEndpoint = endpoint.startsWith('/api/') ? endpoint.substring(4) : endpoint.substring(3);
    }
    const url = `${this.baseURL}${normalizedEndpoint}`;
    
    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${secureTokenManager.getAdminSessionToken()}`,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred');
    }
  }

  /**
   * Get notification history with pagination and filters
   */
  async getNotificationHistory(filters: NotificationHistoryFilters = {}): Promise<NotificationHistoryResponse> {
    const params = new URLSearchParams();
    
    // Add pagination parameters (always include defaults)
    params.append('page', (filters.page || 1).toString());
    params.append('limit', (filters.limit || 15).toString()); // Max 15 per page
    
    // Add type filter (only if specified)
    if (filters.type && filters.type.trim()) {
      params.append('type', filters.type.trim());
    }
    
    // Add contactNumber filter (only if specified)
    if (filters.contactNumber && filters.contactNumber.trim()) {
      params.append('contactNumber', filters.contactNumber.trim());
    }

    const queryString = params.toString();
    const endpoint = `/api/notifications/history?${queryString}`;
    
    return this.makeRequest<NotificationHistoryResponse>(endpoint);
  }
}
